import { PixelizeThemeProvider, useThemeMode } from 'pixelize-design-library';
import { useEffect, useRef, type ReactNode } from 'react';

import { AppErrorBoundary } from '@core/components/Error/AppErrorBoundary';
import { useLanguageSync } from '@core/i18n/useLanguageSync';
import { settings } from '@core/store/Settings/settingsSlice';
import { useAppDispatch, useAppSelector } from '@core/store/hooks';
import { AppRouter } from '@core/router';

import { AppAccentScope } from './AppAccentScope';
import { APP_THEME_OVERRIDES } from './themeOverrides';

/**
 * `defaultMode` only SEEDS the provider. A preference that arrives after first mount —
 * hydrated from storage, or restored on a second render pass — is never picked up, so it
 * is pushed in once with `setMode`. Guarded by a ref: running it on every change turns it
 * into the mirror that flickers.
 */
function ThemedAppSync(): null {
  const { mode, setMode } = useThemeMode();
  const storedMode = useAppSelector((s) => s.settings.colorMode);
  const hydrated = useRef(false);

  useEffect(() => {
    if (hydrated.current) return;
    hydrated.current = true;
    if (storedMode !== mode) setMode(storedMode);
  }, [storedMode, mode, setMode]);

  useLanguageSync();
  return null;
}

/**
 * The ONE provider. No second `ChakraProvider` (it doubles every toast portal), no second
 * `Toaster` (the provider already renders one), and no `ColorModeScript` (it writes Chakra's
 * own colour-mode key back in, and this library adapts by swapping the palette instead).
 */
export function ThemedApp({ children }: { children?: ReactNode }) {
  const dispatch = useAppDispatch();
  const brand = useAppSelector((s) => s.settings.theme);
  const colorMode = useAppSelector((s) => s.settings.colorMode);

  return (
    <PixelizeThemeProvider
      brand={brand}
      defaultMode={colorMode}
      disablePersistence
      onModeChange={(mode) => dispatch(settings({ colorMode: mode }))}
      onBrandChange={(theme) => dispatch(settings({ theme }))}
      themeOverrides={APP_THEME_OVERRIDES}
    >
      <ThemedAppSync />
      <AppAccentScope>
        <AppErrorBoundary>{children ?? <AppRouter />}</AppErrorBoundary>
      </AppAccentScope>
    </PixelizeThemeProvider>
  );
}
